import { createFileRoute, redirect, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { LayoutDashboard, Building2, CreditCard, Activity, ServerCog, BookOpen, LogOut, Layers, Bot, Truck, Calculator, Megaphone } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth-store'
import { adminApi } from '@/lib/api'
import { Badge } from '@/components/ui'

export const Route = createFileRoute('/_app')({
  // DEMO: el panel plataforma (superadmin) está bloqueado. Cualquier ruta /_app/*
  // redirige al store demo. Si más adelante quieren habilitarlo, basta con
  // remover el redirect.
  beforeLoad: () => {
    throw redirect({ to: '/store/$id', params: { id: 'tnt_demo' } })
  },
  component: AppLayout,
})

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/tenants', label: 'Tiendas', icon: Building2 },
  { to: '/plans', label: 'Planes', icon: Layers },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/tarifas', label: 'Tarifas', icon: Calculator },
  { to: '/marketing', label: 'Marketing', icon: Megaphone },
  { to: '/logistics', label: 'Logística', icon: Truck },
  { to: '/activity', label: 'Actividad', icon: Activity },
  { to: '/agents', label: 'Agentes IA', icon: Bot },
  { to: '/system', label: 'Sistema', icon: ServerCog },
  { to: '/docs', label: 'Docs', icon: BookOpen },
]

function AppLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout: clearAuth } = useAuth()

  // Health ping cada 60s
  const { data: dashboardData } = useQuery({
    queryKey: ['platform-dashboard'],
    queryFn: () =>
      adminApi.get<{ data: { tenants: { total: number; active: number }; system: { healthy: boolean } } }>(
        '/api/admin/dashboard',
      ),
    refetchInterval: 60_000,
  })

  function logout() {
    clearAuth()
    navigate({ to: '/login' })
  }

  return (
    <div className="flex h-screen bg-background text-foreground">
      <aside className="w-64 border-r border-border bg-[hsl(var(--surface))] flex flex-col">
        <div className="px-6 py-5 border-b border-border">
          <h1 className="text-lg font-bold tracking-tight">Core Platform</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Panel de administración</p>
        </div>

        <div className="px-6 py-3 border-b border-border space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Tiendas activas</span>
            <span className="font-mono text-foreground">
              {dashboardData ? `${dashboardData.data.tenants.active} / ${dashboardData.data.tenants.total}` : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Sistema</span>
            <Badge variant={dashboardData?.data.system.healthy ? 'success' : 'destructive'}>
              {dashboardData?.data.system.healthy ? 'OK' : 'Error'}
            </Badge>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, exact }) => {
            const active = exact
              ? location.pathname === to
              : (location.pathname === to || location.pathname.startsWith(to + '/'))
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition',
                  active
                    ? 'bg-[hsl(var(--muted))] text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]',
                )}
              >
                <Icon className="size-4" />
                {label}
              </Link>
            )
          })}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          <Badge variant="muted" className="w-full justify-center">v0.1.0 · platform</Badge>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition"
          >
            <LogOut className="size-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}
